import * as THREE from 'three';
import { clamp } from '../core/noise';
import { START_X, START_Z, terrainGradient, terrainHeight } from '../world/terrain';
import type { World } from '../world/World';
import { setSkiRigSteering } from './createSkiRig';

const GRAVITY = 9.81;
const SKIER_RADIUS = 0.34;

export function carveTurnRate(speed: number, steering: number): number {
  const edge = Math.abs(clamp(steering, -1, 1));
  const speedAuthority = clamp(1.42 - speed * 0.009, 0.9, 1.31);
  return Math.sign(steering) * speedAuthority * Math.pow(edge, 1.78);
}

export function carveDrag(speed: number, steering: number): number {
  const edge = Math.abs(clamp(steering, -1, 1));
  return Math.pow(edge, 2.35) * (3.2 + speed * 0.28);
}

export class Skier {
  x = START_X;
  z = START_Z;
  y = terrainHeight(START_X, START_Z);
  speed = 17;
  heading = 0;
  grounded = true;

  private verticalSpeed = -2.5;
  private cameraPitch = -0.08;
  private cameraRoll = 0;
  private elapsed = 0;
  private lastCollisionAt = -10;
  private landingBounce = 0;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly skiRig: THREE.Group,
    private readonly world: World,
  ) {}

  update(delta: number, steering: number): void {
    this.elapsed += delta;
    const directionX = Math.sin(this.heading);
    const directionZ = -Math.cos(this.heading);
    const gradient = terrainGradient(this.x, this.z);
    const downhill = -(gradient.x * directionX + gradient.z * directionZ);

    this.heading += carveTurnRate(this.speed, steering) * delta;

    const gravityAcceleration = GRAVITY * clamp(downhill, -0.18, 0.58);
    const airDrag = this.speed * this.speed * 0.00315;
    const turnDrag = carveDrag(this.speed, steering);
    this.speed = clamp(this.speed + (gravityAcceleration + 0.72 - airDrag - turnDrag) * delta, 2.2, 38);

    const nextDirectionX = Math.sin(this.heading);
    const nextDirectionZ = -Math.cos(this.heading);
    const nextX = this.x + nextDirectionX * this.speed * delta;
    const nextZ = this.z + nextDirectionZ * this.speed * delta;
    const nextGround = terrainHeight(nextX, nextZ);

    if (this.grounded) {
      const projectedY = this.y + this.verticalSpeed * delta - GRAVITY * delta * delta * 0.5;
      if (nextGround < projectedY - 0.22) {
        this.grounded = false;
        this.y = projectedY;
        this.verticalSpeed -= GRAVITY * delta;
      } else {
        const climb = nextGround - this.y;
        if (climb > 0.18) this.speed = Math.max(5.5, this.speed - climb * 0.85);
        this.y = nextGround;
        const nextGradient = terrainGradient(nextX, nextZ);
        this.verticalSpeed = clamp(
          (nextGradient.x * nextDirectionX + nextGradient.z * nextDirectionZ) * this.speed,
          -13,
          8,
        );
      }
    } else {
      this.y += this.verticalSpeed * delta;
      this.verticalSpeed -= GRAVITY * delta;
      if (this.y <= nextGround) {
        const impact = Math.abs(this.verticalSpeed);
        this.y = nextGround;
        this.grounded = true;
        this.speed *= clamp(1 - impact * 0.012, 0.72, 0.98);
        this.landingBounce = clamp(impact * 0.012, 0.02, 0.16);
      }
    }

    this.x = nextX;
    this.z = nextZ;
    this.resolveCollisions();
    this.updateCamera(delta, steering);
  }

  private resolveCollisions(): void {
    for (const obstacle of this.world.obstaclesNear(this.x, this.z)) {
      const dx = this.x - obstacle.x;
      const dz = this.z - obstacle.z;
      const minimumDistance = SKIER_RADIUS + obstacle.radius;
      const distanceSquared = dx * dx + dz * dz;
      if (distanceSquared >= minimumDistance * minimumDistance) continue;

      const distance = Math.sqrt(Math.max(distanceSquared, 0.0001));
      const normalX = dx / distance;
      const normalZ = dz / distance;
      this.x = obstacle.x + normalX * minimumDistance;
      this.z = obstacle.z + normalZ * minimumDistance;
      this.y = Math.max(this.y, terrainHeight(this.x, this.z));
      this.speed *= obstacle.kind === 'tree' ? 0.48 : 0.66;

      const directionX = Math.sin(this.heading);
      const directionZ = -Math.cos(this.heading);
      const side = directionX * normalZ - directionZ * normalX;
      this.heading += (side >= 0 ? 0.28 : -0.28);
      this.landingBounce = 0.12;

      if (this.elapsed - this.lastCollisionAt > 0.35) {
        if (navigator.userActivation?.hasBeenActive) navigator.vibrate?.(12);
        this.lastCollisionAt = this.elapsed;
      }
    }
  }

  private updateCamera(delta: number, steering: number): void {
    const gradient = terrainGradient(this.x, this.z);
    const directionX = Math.sin(this.heading);
    const directionZ = -Math.cos(this.heading);
    const groundHeight = terrainHeight(this.x, this.z);
    const surfacePitch = Math.atan(gradient.x * directionX + gradient.z * directionZ);
    const airbornePitch = this.grounded ? surfacePitch : Math.atan2(this.verticalSpeed, Math.max(this.speed, 1));
    const targetPitch = clamp(airbornePitch, -0.48, 0.2);
    const targetRoll = -steering * 0.13;
    const pitchResponse = this.grounded ? 3.8 : 1.7;
    this.cameraPitch += (targetPitch - this.cameraPitch) * (1 - Math.exp(-pitchResponse * delta));
    this.cameraRoll += (targetRoll - this.cameraRoll) * (1 - Math.exp(-5.5 * delta));

    this.landingBounce *= Math.exp(-7 * delta);
    const bob = this.grounded ? Math.sin(this.elapsed * this.speed * 0.34) * 0.012 : 0;
    this.camera.position.set(0, this.y - groundHeight + 1.62 + bob - this.landingBounce, 0);
    this.camera.rotation.set(this.cameraPitch, -this.heading, this.cameraRoll, 'YXZ');

    setSkiRigSteering(this.skiRig, steering);
  }
}
